/**
 * Contract registry. Contract type codes are Deriv's (proposal/buy `contract_type`). Duration
 * limits here are UI defaults only — the broker enforces its own on every proposal.
 */

export const MODALITIES = ['EVEN_ODD', 'RISE_FALL', 'DIGITS', 'MATCH_DIFF', 'OVER_UNDER', 'ACCUMULATOR'] as const;
export type Modality = (typeof MODALITIES)[number];

/**
 * Modalities offered by the platform. MATCH_DIFF and OVER_UNDER stay in the
 * registry (the architecture supports them) but are disabled by product
 * decision: the backend rejects them and the UI does not show them.
 */
export const ENABLED_MODALITIES: readonly Modality[] = ['RISE_FALL', 'EVEN_ODD'];

/**
 * Modalities the platform trades at Deriv: Dígitos (real Par/Ímpar: DIGITEVEN/DIGITODD) and Sobe e
 * Desce (CALL/PUT). DIGITS was the previous broker's Dígitos (a Sobe/Desce shown as Par/Ímpar): it
 * stays readable in the history, never traded. Rush (ACCU) stays off until it is switched on.
 */
export function tradableModalities(): readonly Modality[] {
  return ['RISE_FALL', 'EVEN_ODD'];
}

export function isModalityEnabled(m: Modality): boolean {
  return tradableModalities().includes(m);
}

/** Games a robot plays (/automacao, Meus robôs, Estratégias): Dígitos and Sobe e Desce. */
export const ROBOT_MODALITIES: readonly Modality[] = ['EVEN_ODD', 'RISE_FALL'];

/** How a robot entry is traded: exactly like the /operar game, as it is read (Dígitos = Par/Ímpar). */
export function robotTrade(modality: Modality, direction: Direction): { modality: Modality; direction: Direction } {
  return { modality, direction };
}

/**
 * The robot's own side of a traded entry. A Dígitos robot of the previous broker bought DIGITS
 * (Par = CALL, Ímpar = PUT): its old entries still read as Par/Ímpar.
 */
export function robotSide(modality: Modality, traded: Direction): Direction {
  if (modality === 'EVEN_ODD') return traded === 'RISE' ? 'EVEN' : traded === 'FALL' ? 'ODD' : traded;
  return traded;
}

export const DIRECTIONS = ['EVEN', 'ODD', 'RISE', 'FALL', 'MATCH', 'DIFF', 'OVER', 'UNDER', 'ACCU'] as const;
export type Direction = (typeof DIRECTIONS)[number];

export type BrokerContractType =
  | 'DIGITEVEN'
  | 'DIGITODD'
  | 'CALL'
  | 'PUT'
  | 'DIGITMATCH'
  | 'DIGITDIFF'
  | 'DIGITOVER'
  | 'DIGITUNDER'
  | 'ACCU';

export type DurationUnit = 't' | 's' | 'm' | 'h' | 'd';

export interface DirectionSpec {
  direction: Direction;
  contractType: BrokerContractType;
  label: string;
  /** Barrier digit range for digit contracts that need one. */
  barrier?: { min: number; max: number; default: number };
}

export interface ModalitySpec {
  modality: Modality;
  label: string;
  shortLabel: string;
  description: string;
  /** contract_category from contracts_for. */
  category: 'digits' | 'callput' | 'accumulator';
  /** Two opposite sides, except the Acumulador (one contract, no side). */
  directions: readonly [DirectionSpec, ...DirectionSpec[]];
  durationUnits: readonly DurationUnit[];
  defaultDuration: { value: number; unit: DurationUnit };
  ticks: { min: number; max: number };
}

export const MODALITY_SPECS: Record<Modality, ModalitySpec> = {
  EVEN_ODD: {
    modality: 'EVEN_ODD',
    label: 'Dígitos Par/Ímpar',
    shortLabel: 'Par/Ímpar',
    description: 'Prevê se o último dígito da cotação no fechamento da rodada será par ou ímpar.',
    category: 'digits',
    directions: [
      { direction: 'EVEN', contractType: 'DIGITEVEN', label: 'Par' },
      { direction: 'ODD', contractType: 'DIGITODD', label: 'Ímpar' },
    ],
    durationUnits: ['t'],
    defaultDuration: { value: 1, unit: 't' },
    ticks: { min: 1, max: 10 },
  },
  RISE_FALL: {
    modality: 'RISE_FALL',
    label: 'Sobe/Desce',
    shortLabel: 'Sobe/Desce',
    description: 'Prevê se a cotação no fechamento da rodada (15s) ficará acima ou abaixo da abertura.',
    category: 'callput',
    directions: [
      { direction: 'RISE', contractType: 'CALL', label: 'Sobe' },
      { direction: 'FALL', contractType: 'PUT', label: 'Desce' },
    ],
    durationUnits: ['t', 's', 'm', 'h'],
    defaultDuration: { value: 5, unit: 't' },
    ticks: { min: 1, max: 10 },
  },
  // After RISE_FALL: contract type CALL/PUT alone still reads as Sobe/Desce (orders keep their modality).
  // The previous broker's Dígitos (history only, never traded at Deriv): a 5s Sobe/Desce shown as Par/Ímpar.
  DIGITS: {
    modality: 'DIGITS',
    label: 'Dígitos',
    shortLabel: 'Dígitos',
    description: 'Dígitos da corretora anterior: Par quando a cotação fechava acima da abertura, Ímpar quando fechava abaixo.',
    category: 'callput',
    // Par is the CALL (price up), Ímpar the PUT.
    directions: [
      { direction: 'RISE', contractType: 'CALL', label: 'Par' },
      { direction: 'FALL', contractType: 'PUT', label: 'Ímpar' },
    ],
    durationUnits: ['s'],
    defaultDuration: { value: 5, unit: 's' },
    ticks: { min: 0, max: 0 },
  },
  MATCH_DIFF: {
    modality: 'MATCH_DIFF',
    label: 'Dígito Igual/Diferente',
    shortLabel: 'Igual/Dif.',
    description: 'Prevê se o último dígito será igual ou diferente do dígito escolhido.',
    category: 'digits',
    directions: [
      { direction: 'MATCH', contractType: 'DIGITMATCH', label: 'Igual', barrier: { min: 0, max: 9, default: 5 } },
      { direction: 'DIFF', contractType: 'DIGITDIFF', label: 'Diferente', barrier: { min: 0, max: 9, default: 5 } },
    ],
    durationUnits: ['t'],
    defaultDuration: { value: 5, unit: 't' },
    ticks: { min: 1, max: 10 },
  },
  OVER_UNDER: {
    modality: 'OVER_UNDER',
    label: 'Dígito Acima/Abaixo',
    shortLabel: 'Acima/Abaixo',
    description: 'Prevê se o último dígito será maior ou menor que o dígito escolhido.',
    category: 'digits',
    directions: [
      { direction: 'OVER', contractType: 'DIGITOVER', label: 'Acima', barrier: { min: 0, max: 8, default: 4 } },
      { direction: 'UNDER', contractType: 'DIGITUNDER', label: 'Abaixo', barrier: { min: 1, max: 9, default: 5 } },
    ],
    durationUnits: ['t'],
    defaultDuration: { value: 5, unit: 't' },
    ticks: { min: 1, max: 10 },
  },
  ACCUMULATOR: {
    modality: 'ACCUMULATOR',
    label: 'Rush',
    shortLabel: 'Rush',
    description: 'A entrada cresce a cada tick em que a cotação fica dentro da faixa. Saque quando quiser; se a cotação romper a faixa, a entrada é perdida.',
    category: 'accumulator',
    directions: [{ direction: 'ACCU', contractType: 'ACCU', label: 'Rush' }],
    // No expiry: the contract runs until it is sold, takes its profit or the price breaks the corridor.
    durationUnits: [],
    defaultDuration: { value: 0, unit: 't' },
    ticks: { min: 0, max: 0 },
  },
};

export function directionSpec(modality: Modality, direction: Direction): DirectionSpec {
  const spec = MODALITY_SPECS[modality].directions.find((d) => d.direction === direction);
  if (!spec) throw new Error(`Direction ${direction} is not valid for ${modality}`);
  return spec;
}

export function contractTypeFor(modality: Modality, direction: Direction): BrokerContractType {
  return directionSpec(modality, direction).contractType;
}

export function modalityForContractType(ct: string): { modality: Modality; direction: Direction } | null {
  for (const spec of Object.values(MODALITY_SPECS)) {
    for (const d of spec.directions) {
      if (d.contractType === ct) return { modality: spec.modality, direction: d.direction };
    }
  }
  return null;
}

/** Name of a side on screen; with the modality, that modality's own (Dígitos: Par/Ímpar). */
export function directionLabel(direction: Direction, modality?: Modality | null): string {
  const own = modality ? MODALITY_SPECS[modality]?.directions.find((x) => x.direction === direction) : undefined;
  if (own) return own.label;
  for (const spec of Object.values(MODALITY_SPECS)) {
    const d = spec.directions.find((x) => x.direction === direction);
    if (d) return d.label;
  }
  return direction;
}

export function needsBarrier(modality: Modality, direction: Direction): boolean {
  return Boolean(directionSpec(modality, direction).barrier);
}

export function validateBarrier(modality: Modality, direction: Direction, barrier: number | undefined): string | null {
  const spec = directionSpec(modality, direction).barrier;
  if (!spec) return null;
  if (barrier === undefined || !Number.isInteger(barrier)) return 'Escolha um dígito.';
  if (barrier < spec.min || barrier > spec.max) return `Dígito deve estar entre ${spec.min} e ${spec.max}.`;
  return null;
}

export function formatDuration(value: number, unit: DurationUnit): string {
  const names: Record<DurationUnit, [string, string]> = {
    t: ['tick', 'ticks'],
    s: ['segundo', 'segundos'],
    m: ['minuto', 'minutos'],
    h: ['hora', 'horas'],
    d: ['dia', 'dias'],
  };
  const [one, many] = names[unit];
  return `${value} ${value === 1 ? one : many}`;
}

/** Parse duration strings like "15s", "1m", "1d" (contracts_for limits). */
export function parseDuration(raw: string): { value: number; unit: DurationUnit } | null {
  const m = /^(\d+)([tsmhd])$/.exec(raw.trim());
  if (!m) return null;
  return { value: Number(m[1]), unit: m[2] as DurationUnit };
}
